
import { supabase } from '@/integrations/supabase/client';

export type AccountStatus = 'active' | 'warned' | 'suspended' | 'banned';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  subscription_tier: 'free' | 'standard' | 'premium' | 'ultimate';
  created_at: string;
  updated_at: string;
  bio: string | null;
  location: string | null;
  admin_role: string;
  account_status: AccountStatus;
  suspension_reason: string | null;
  suspended_until: string | null;
  strike_count?: number;
  last_infraction_date?: string | null;
  premium_trial_ends_at?: string | null;
  subscription_ends_at?: string | null;
  custom_pet_limit?: number | null;
}

// Get all users (admin only)
export const getAllUsers = async (): Promise<AdminUser[]> => {
  const { data, error } = await supabase.rpc('admin_get_all_users');
  
  if (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
  
  return (data || []).map((user: any) => ({
    id: user.id,
    email: user.email,
    full_name: user.full_name || '',
    avatar_url: user.avatar_url,
    subscription_tier: user.subscription_tier,
    created_at: user.created_at,
    updated_at: user.updated_at,
    bio: user.bio,
    location: user.location,
    admin_role: user.admin_role || 'none',
    account_status: user.account_status || 'active',
    suspension_reason: user.suspension_reason,
    suspended_until: user.suspended_until,
    strike_count: user.strike_count || 0,
    last_infraction_date: user.last_infraction_date,
    premium_trial_ends_at: user.premium_trial_ends_at || null,
    subscription_ends_at: user.subscription_ends_at || null,
    custom_pet_limit: user.custom_pet_limit || null,
  }));
};

// Search users by term (admin only)
export const searchUsers = async (searchTerm: string): Promise<AdminUser[]> => {
  const { data, error } = await supabase.rpc('admin_search_users', {
    search_term: searchTerm
  });
  
  if (error) {
    console.error('Error searching users:', error);
    throw error;
  }
  
  return (data || []).map((user: any) => ({
    id: user.id,
    email: user.email,
    full_name: user.full_name || '',
    avatar_url: user.avatar_url,
    subscription_tier: user.subscription_tier,
    created_at: user.created_at,
    updated_at: user.updated_at || user.created_at,
    bio: user.bio || null,
    location: user.location || null,
    admin_role: user.admin_role || 'none',
    account_status: user.account_status || 'active',
    suspension_reason: user.suspension_reason,
    suspended_until: user.suspended_until,
    strike_count: user.strike_count || 0,
    last_infraction_date: user.last_infraction_date,
    premium_trial_ends_at: user.premium_trial_ends_at || null,
    subscription_ends_at: user.subscription_ends_at || null,
    custom_pet_limit: user.custom_pet_limit || null,
  }));
};

// Update user profile (admin only)
export const updateUserProfile = async (
  userId: string,
  updates: {
    full_name?: string;
    subscription_tier?: 'free' | 'standard' | 'premium' | 'ultimate';
    admin_role?: string;
    premium_trial_ends_at?: string | null;
    subscription_ends_at?: string | null;
    custom_pet_limit?: number | null;
    custom_medical_record_limit?: number | null;
  }
) => {
  const { data, error } = await supabase.rpc('admin_update_user_profile', {
    target_user_id: userId,
    new_full_name: updates.full_name || null,
    new_subscription_tier: updates.subscription_tier || null,
    new_admin_role: updates.admin_role || null,
    new_trial_ends_at: updates.premium_trial_ends_at === null ? '1970-01-01T00:00:00Z' : (updates.premium_trial_ends_at || null),
    new_subscription_ends_at: updates.subscription_ends_at === null ? '1970-01-01T00:00:00Z' : (updates.subscription_ends_at || null),
    new_custom_pet_limit: updates.custom_pet_limit === null ? -999 : (updates.custom_pet_limit || null),
    new_custom_medical_record_limit: updates.custom_medical_record_limit === null ? -999 : (updates.custom_medical_record_limit || null),
  } as any);
  
  if (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
  
  return data;
};

// Delete user account (admin only)
export const deleteUserAccount = async (userId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('admin_delete_user_account', {
    target_user_id: userId
  });
  
  if (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
  
  return data;
};

// Check if current user is admin
export const checkIsAdmin = async (): Promise<boolean> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return false;

  const { data, error } = await supabase.rpc('is_admin', {
    user_uuid: user.user.id
  });

  if (error) {
    console.error('Error checking admin status:', error);
    return false;
  }

  return data === true;
};

// Suspend user account (admin only)
export const suspendUserAccount = async (
  userId: string,
  reason: string,
  suspendUntil?: Date
): Promise<void> => {
  const { error } = await supabase.rpc('admin_suspend_account', {
    target_user_id: userId,
    suspend_reason: reason,
    suspend_until: suspendUntil?.toISOString() || null
  });

  if (error) {
    console.error('Error suspending user account:', error);
    throw error;
  }
};

// Ban user account (admin only)
export const banUserAccount = async (
  userId: string,
  reason: string
): Promise<void> => {
  const { error } = await supabase.rpc('admin_ban_account', {
    target_user_id: userId,
    ban_reason: reason
  });

  if (error) {
    console.error('Error banning user account:', error);
    throw error;
  }
};

// Reactivate user account (admin only)
export const reactivateUserAccount = async (userId: string): Promise<void> => {
  const { error } = await supabase.rpc('admin_reactivate_account', {
    target_user_id: userId
  });

  if (error) {
    console.error('Error reactivating user account:', error);
    throw error;
  }
};
